import { useCallback, useMemo } from 'react'
import { toast } from 'sonner'
import type { AuthQuotaMetric, AuthQuotaSummary } from '@/features/auth/types'
import { useAuthSessionStore } from '@/features/auth/stores/auth-session-store'
import { useMembershipDialogStore } from '@/features/billing/stores/membership-dialog-store'

type UseMembershipFeatureGateOptions = {
  requiredModules?: string[]
  requiredQuotaMetric?: AuthQuotaMetric
  missingMessage: string
  quotaExceededMessage?: string
  unauthenticatedMessage?: string
}

function getQuotaRemaining(
  quotas: AuthQuotaSummary | null | undefined,
  metric: AuthQuotaMetric,
): number | null {
  if (!quotas) {
    return null
  }

  if (typeof quotas.raw[metric] === 'number') {
    return quotas.raw[metric] ?? null
  }

  if (metric === 'ai_processing_seconds') {
    return quotas.aiProcessingSecondsRemaining
  }

  return null
}

export function useMembershipFeatureGate({
  requiredModules = [],
  requiredQuotaMetric,
  missingMessage,
  quotaExceededMessage = '当前 AI 处理时长已用完，请升级套餐或等待额度重置后再试。',
  unauthenticatedMessage = '请先登录后再继续此操作。',
}: UseMembershipFeatureGateOptions) {
  const session = useAuthSessionStore((state) => state.session)
  const openMembershipDialog = useMembershipDialogStore((state) => state.openDialog)

  const ownedModules = useMemo(
    () => [
      ...(session?.membership?.appFeatureFlags ?? []),
      ...(session?.membership?.aiEntitlements ?? []),
      ...(session?.membership?.automationEntitlements ?? []),
    ],
    [
      session?.membership?.aiEntitlements,
      session?.membership?.appFeatureFlags,
      session?.membership?.automationEntitlements,
    ],
  )

  const remainingQuota = useMemo(
    () =>
      requiredQuotaMetric
        ? getQuotaRemaining(session?.membership?.quotas, requiredQuotaMetric)
        : null,
    [requiredQuotaMetric, session?.membership?.quotas],
  )

  const hasRequiredModules = useMemo(
    () => requiredModules.every((moduleCode) => ownedModules.includes(moduleCode)),
    [ownedModules, requiredModules],
  )

  const hasRequiredQuota = useMemo(
    () => !requiredQuotaMetric || remainingQuota === null || remainingQuota > 0,
    [remainingQuota, requiredQuotaMetric],
  )

  const hasAccess = hasRequiredModules && hasRequiredQuota

  const ensureAccess = useCallback(() => {
    if (!session?.user?.id) {
      toast.error(unauthenticatedMessage)
      return false
    }

    if (!hasRequiredModules) {
      toast.error(missingMessage)
      openMembershipDialog()
      return false
    }

    if (!hasRequiredQuota) {
      toast.error(quotaExceededMessage)
      openMembershipDialog()
      return false
    }

    return true
  }, [
    hasRequiredModules,
    hasRequiredQuota,
    missingMessage,
    openMembershipDialog,
    quotaExceededMessage,
    session?.user?.id,
    unauthenticatedMessage,
  ])

  return {
    hasAccess,
    ensureAccess,
    remainingQuota,
  }
}
